import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  DestroyRef,
  ElementRef,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { debounceTime } from 'rxjs/operators';

import { WindowService } from 'src/shared/providers/window.service';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { TidyStorage } from 'src/shared/providers/tidy-storage';
import { Util } from 'src/shared/util/util';
import { AboutThisPageService } from 'src/providers/about-this-page/about-this-page.service';
import { OnboardingProvider } from 'src/providers/onboarding/onboarding.provider';
import { DemoBlockService } from 'src/shared/services/demo-block.service';
import { AddUpsellPage } from 'src/pages/upsells/add-upsell/add-upsell';
import { UpsellSettingsPage } from 'src/pages/upsells/upsell-settings/upsell-settings';
import { IntegratePage } from 'src/pages/upsells/integrate/integrate';
import { ConciergeQueriesService } from 'src/shared/services/concierge-queries.service';
import { Loading } from 'src/shared/components/loading/loading';
import { Client } from 'src/providers/client/client';
import { IonicModule } from '@ionic/angular';
import { TidyUiComponentsModule } from 'src/tidy-ui-components/tidy-ui-components.module';
import { SharedModule } from 'src/shared/shared.module';
import { MarketingUpsellsService, UpsellTableRow } from 'src/shared/services/marketing-upsells.service';

@Component({
  selector: 'upsells-tab',
  templateUrl: './upsells-tab.component.html',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IonicModule,
    TidyUiComponentsModule,
    SharedModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class UpsellsTabComponent implements OnInit, OnDestroy {
  headers = ['Property', 'Reservation', 'Request', 'last-column'];
  allRows: UpsellTableRow[] = [];
  rows: UpsellTableRow[] = [];
  loaded = false;
  showLoadingSpinner = false;
  errorMessage = '';
  showAboutThisPage = false;
  totalUpsells = '0';
  overlayTop: string;
  private _upsellsHeader: ElementRef;
  private overlayResizeObserver: ResizeObserver;
  @ViewChild('upsellsHeader', { read: ElementRef }) set upsellsHeader(el: ElementRef) {
    this._upsellsHeader = el;
    if (el && this.showAboutThisPage && !this.overlayResizeObserver) {
      this.setupOverlayObserver();
    }
  }

  private addressResponse: any;
  private panelClosedSubscription$?: Subscription;

  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly navCtrl = inject(CustomNavController);
  private readonly rightSidePanelService = inject(RightSidePanelService);
  private readonly storage = inject(TidyStorage);
  readonly windowService = inject(WindowService);
  private readonly util = inject(Util);
  private readonly aboutThisPageService = inject(AboutThisPageService);
  private readonly onboardingProvider = inject(OnboardingProvider);
  private readonly demoBlock = inject(DemoBlockService);
  private readonly conciergeQueries = inject(ConciergeQueriesService);
  private readonly client = inject(Client);
  private readonly upsellsService = inject(MarketingUpsellsService);

  searchForm = this.fb.group({ search: [''] });

  constructor() {
    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('upsells');
  }

  async ngOnInit() {
    this.searchForm.get('search')!.valueChanges.pipe(
      debounceTime(300),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(value => {
      this.filterRows(value || '');
      this.cdr.markForCheck();
    });

    try {
      this.addressResponse = await this.client.getAddresses();
      this.loadPage();
    } catch (err: any) {
      this.util.showError(
        err.error?.message || err.message,
        10000
      );
    }
  }

  private setupOverlayObserver() {
    const updateTop = () => {
      if (!this._upsellsHeader) {return;}
      const top = this._upsellsHeader.nativeElement.getBoundingClientRect().top - 8;
      this.overlayTop = `${top  }px`;
      this.cdr.markForCheck();
    };
    this.overlayResizeObserver = new ResizeObserver(() => updateTop());
    this.overlayResizeObserver.observe(document.body);
    setTimeout(() => updateTop());
  }

  dismissAboutThisPage() {
    this.showAboutThisPage = false;
    this.overlayResizeObserver?.disconnect();
    this.allRows = [];
    this.rows = [];
    this.cdr.markForCheck();
    this.loadPage(true);
  }

  async loadPage(showLoadingSpinner = false) {
    if (showLoadingSpinner) {
      this.showLoadingSpinner = true;
      this.cleanup();
    }

    this.showAboutThisPage = this.aboutThisPageService.checkIfShouldShowAboutThisPage('upsells');
    this.rows = [];
    this.errorMessage = '';

    try {
      await this.getUpsells();
    } catch (err: any) {
      this.handleError(err);
    }

    this.showLoadingSpinner = false;
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.overlayResizeObserver?.disconnect();
    this.cleanup();
  }

  cleanup() {
    this.panelClosedSubscription$?.unsubscribe();
  }

  @Loading('', true)
  async getUpsells() {
    try {
      const result = await this.upsellsService.fetchUpsells(this.showAboutThisPage);
      this.totalUpsells = result.total;
      this.allRows = await this.upsellsService.buildRows(result.upsells, this.addressResponse, this.showAboutThisPage);
      this.rows = [...this.allRows];
    } catch (err: any) {
      this.handleError(err);
    }
  }

  private filterRows(searchText: string): void {
    if (!searchText) {
      this.rows = [...this.allRows];
      return;
    }
    const lower = searchText.toLowerCase();
    this.rows = this.allRows.filter(row =>
      row['Property']?.value?.toLowerCase().includes(lower) ||
      row['Reservation']?.value?.toLowerCase().includes(lower) ||
      row['Request']?.value?.toLowerCase().includes(lower)
    );
  }

  clearSearch(): void {
    if (this.showAboutThisPage) {return;}
    this.searchForm.get('search')?.setValue('');
    this.rows = [...this.allRows];
    this.cdr.markForCheck();
  }

  sortChanged(sort: any): void {
    if (this.showAboutThisPage) {return;}
    const multiplier = sort.direction === 'asc' ? 1 : -1;
    this.rows = [...this.rows].sort((a, b) => {
      if (a[sort.active] && b[sort.active]) {
        if (a[sort.active].value < b[sort.active].value) {return -1 * multiplier;}
        if (a[sort.active].value > b[sort.active].value) {return 1 * multiplier;}
      }
      return 0;
    });
    this.cdr.markForCheck();
  }

  handleError(err: any) {
    if (!this.showAboutThisPage) {
      this.errorMessage = err.error?.message || err.message;
    }
  }

  goToAddUpsellPage() {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) {return;}

    this.storage.save('editingUpsellData', null);
    this.storage.save('editingUpsellId', null);

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(AddUpsellPage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .panelClosed()
        .subscribe(() => this.refreshData());
    } else {
      this.navCtrl.navigateForward('add-upsell');
    }
  }

  goToEditUpsellPage(upsellData: any, upsellId: string) {
    if (this.showAboutThisPage) {return;}

    this.storage.save('editingUpsellData', upsellData);
    this.storage.save('editingUpsellId', upsellId);

    const params = {
      editingUpsellData: upsellData,
      editingUpsellId: upsellId
    };

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.navigateTo('add-upsell', params, AddUpsellPage, false);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(() => this.refreshData());
    } else {
      this.navCtrl.navigateForward('add-upsell', params);
    }
  }

  onTableColumnClick(event: { column: string; element: any }) {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) {return;}

    if (event.element['last-column']?.value) {
      const upsellId = event.element['last-column'].value;
      const upsellData = {
        property: event.element['Property']?.addressId || event.element['Property']?.value,
        reservation: event.element['Reservation']?.reservationId,
        requestType: this.upsellsService.getRequestTypeKeyFromDisplay(event.element['Request']?.value)
      };

      this.goToEditUpsellPage(upsellData, upsellId);
    }
  }

  async onPageChange() {
    if (this.showAboutThisPage || this.allRows.length >= parseInt(this.totalUpsells)) {return;}

    try {
      const nextPage = Math.floor(this.allRows.length / 50) + 1;
      const newItems = await this.upsellsService.fetchPaginatedUpsells(nextPage, 50);
      const newRows = await this.upsellsService.buildRows(newItems, this.addressResponse, false);
      this.allRows = [...this.allRows, ...newRows];
      this.rows = [...this.allRows];
      this.cdr.markForCheck();
    } catch (err: any) {
      this.handleError(err);
    }
  }

  exportCSV() {
    if (this.showAboutThisPage) {return;}

    if (this.rows.length > 0) {
      this.util.showSuccess(`Exporting ${this.rows.length} upsells to CSV`, 3000);
    } else {
      this.util.showError('No upsells data to export', 3000);
    }
  }

  openUpsellSettings() {
    if (this.showAboutThisPage) {return;}
    if (this.demoBlock.block()) {return;}

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(UpsellSettingsPage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(() => this.refreshData());
    } else {
      this.navCtrl.navigateForward('upsell-settings');
    }
  }

  openIntegrationInfo() {
    if (this.showAboutThisPage) {return;}

    if (this.windowService.isDesktopRes) {
      this.storage.save('dialog-right-side-open', true);
      this.rightSidePanelService.openRightSidePanel(IntegratePage);
      this.panelClosedSubscription$ = this.rightSidePanelService
        .afterPanelClosed()
        .subscribe(() => this.refreshData());
    } else {
      this.navCtrl.navigateForward('integrate');
    }
  }

  async refreshData() {
    if (this.showAboutThisPage) {return;}

    this.conciergeQueries.invalidateUpsells();
    this.upsellsService.invalidateCaches();
    this.loaded = false;
    this.rows = [];
    await this.loadPage(true);
  }

}
